/**
 * Janko Piano - MIDI Recorder Module
 * Handles recording and playback of MIDI events
 * @version 0.2.67
 */

import { CONFIG } from './config.js';
import { playNote, stopNote } from './audio.js';

// =============================================================================
// STORAGE CONSTANTS
// =============================================================================

const STORAGE_KEY = 'janko_piano_recording';

// =============================================================================
// RECORDING STATE
// =============================================================================

let isRecording = false;
let isPlaying = false;
let recordedEvents = [];
let recordingStartTime = 0;
let playbackTimeouts = [];
let playbackStartTime = 0;

// =============================================================================
// RECORDING FUNCTIONS
// =============================================================================

export function toggleRecording() {
    // If playing MIDI, stop playback instead
    if (isPlaying) {
        stopPlayback();
        return;
    }
    
    if (isRecording) {
        stopRecording();
    } else {
        startRecording();
    }
}

export function startRecording() {
    if (isPlaying) {
        stopPlayback();
    }
    
    isRecording = true;
    recordedEvents = [];
    recordingStartTime = Date.now();
    
    // Update UI
    updateRecordButton();
    
    if (window.showToast) {
        window.showToast('🔴 Recording started');
    }
    
    console.log('[Recorder] Recording started');
}

export function stopRecording() {
    if (!isRecording) return;
    
    isRecording = false;
    const duration = ((Date.now() - recordingStartTime) / 1000).toFixed(1);
    
    // Update UI first
    updateRecordButton();
    
    if (recordedEvents.length === 0) {
        if (window.showToast) {
            window.showToast('⚠️ No notes recorded');
        }
        return;
    }
    
    // Auto-export as MIDI file
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const filename = `janko_${timestamp}.mid`;
    exportMIDI(filename);
    
    if (window.showToast) {
        window.showToast(`💾 Saved ${filename} (${duration}s)`);
    }
    
    console.log(`[Recorder] Recording stopped: ${recordedEvents.length} events, ${duration}s`);
}

export function recordEvent(type, midi, velocity, channel = 0) {
    if (!isRecording) return;
    
    const timestamp = Date.now() - recordingStartTime;
    const event = {
        type,
        timestamp,
        midi,
        velocity,
        channel,
        instrument: CONFIG.currentInstrument || 'piano'
    };
    
    recordedEvents.push(event);
}

export function clearRecording() {
    if (isRecording) {
        stopRecording();
    }
    if (isPlaying) {
        stopPlayback();
    }
    
    recordedEvents = [];
    
    // Remove from storage
    try {
        localStorage.removeItem(STORAGE_KEY);
        console.log('[Recorder] Recording cleared from storage');
    } catch (e) {
        console.error('[Recorder] Failed to clear storage:', e);
    }
    
    updatePlaybackButton();
    
    if (window.showToast) {
        window.showToast('🗑️ Recording cleared');
    }
    
    console.log('[Recorder] Recording cleared');
}

// =============================================================================
// PLAYBACK FUNCTIONS
// =============================================================================

export function togglePlayback() {
    if (isPlaying) {
        stopPlayback();
    } else {
        startPlayback();
    }
}

export function startPlayback() {
    if (recordedEvents.length === 0) {
        if (window.showToast) {
            window.showToast('⚠️ No recording to play');
        }
        return;
    }
    
    if (isRecording) {
        stopRecording();
    }
    
    isPlaying = true;
    playbackStartTime = Date.now();
    
    // Update UI
    updatePlaybackButton();
    updateRecordButton();
    
    if (window.showToast) {
        window.showToast('▶️ Playback started');
    }
    
    console.log(`[Recorder] Playback started: ${recordedEvents.length} events`);
    
    // Schedule all events
    const activeNotes = new Map(); // Track active notes for cleanup
    
    recordedEvents.forEach(event => {
        const timeout = setTimeout(() => {
            if (event.type === 'noteOn') {
                // Pass channel info for multi-channel MIDI playback
                const noteData = playNote(event.midi, event.velocity, event.channel);
                if (noteData) {
                    activeNotes.set(event.midi, noteData);
                }
                
                // Visual feedback - highlight key only if channel matches filter
                const shouldVisualize = CONFIG.visualizeAllChannels || 
                                       CONFIG.currentPlaybackChannel === null || 
                                       event.channel === CONFIG.currentPlaybackChannel;
                
                if (shouldVisualize && window.highlightMIDIKey) {
                    window.highlightMIDIKey(event.midi);
                }
            } else if (event.type === 'noteOff') {
                const noteData = activeNotes.get(event.midi);
                if (noteData) {
                    stopNote(noteData);
                    activeNotes.delete(event.midi);
                }
                
                // Visual feedback - remove highlight only if channel matches filter
                const shouldVisualize = CONFIG.visualizeAllChannels || 
                                       CONFIG.currentPlaybackChannel === null || 
                                       event.channel === CONFIG.currentPlaybackChannel;
                
                if (shouldVisualize && window.unhighlightMIDIKey) {
                    window.unhighlightMIDIKey(event.midi);
                }
            } else if (event.type === 'programChange') {
                // Handle instrument changes per channel
                if (window.getSynth) {
                    const synth = window.getSynth();
                    if (synth && synth.programChange) {
                        synth.programChange(event.channel, event.program);
                        console.log(`[MIDI] Program change: Channel ${event.channel} -> Instrument ${event.program}`);
                    }
                }
            }
        }, event.timestamp);
        
        playbackTimeouts.push(timeout);
    });
    
    // Auto-stop when playback finishes
    const maxTimestamp = Math.max(...recordedEvents.map(e => e.timestamp), 0);
    const finishTimeout = setTimeout(() => {
        // Stop any remaining notes and clear visual highlights
        activeNotes.forEach((noteData, midi) => {
            stopNote(noteData);
            if (window.unhighlightMIDIKey) {
                window.unhighlightMIDIKey(midi);
            }
        });
        activeNotes.clear();
        
        stopPlayback();
        if (window.showToast) {
            window.showToast('⏹️ Playback finished');
        }
    }, maxTimestamp + 100);
    
    playbackTimeouts.push(finishTimeout);
}

export function stopPlayback() {
    if (!isPlaying) return;
    
    isPlaying = false;
    
    // Clear all scheduled timeouts
    playbackTimeouts.forEach(timeout => clearTimeout(timeout));
    playbackTimeouts = [];
    
    // Clear all visual highlights
    if (window.unhighlightMIDIKey) {
        for (let i = 0; i < 128; i++) {
            window.unhighlightMIDIKey(i);
        }
    }
    
    // Update UI
    updatePlaybackButton();
    updateRecordButton();
    
    console.log('[Recorder] Playback stopped');
}

// =============================================================================
// UI UPDATE FUNCTIONS
// =============================================================================

function updateRecordButton() {
    const btn = document.getElementById('hud-record-btn');
    if (!btn) return;
    
    if (isPlaying) {
        // During MIDI playback, show stop button
        btn.classList.remove('recording');
        btn.title = 'Stop Playback';
        btn.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24">
            <rect x="5" y="5" width="14" height="14" rx="2" fill="#FF3B30"/>
        </svg>`;
    } else if (isRecording) {
        btn.classList.add('recording');
        btn.title = 'Stop Recording';
        // Change to square (stop icon) - bigger square
        btn.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24">
            <rect x="5" y="5" width="14" height="14" rx="2" fill="#FF3B30"/>
        </svg>`;
    } else {
        btn.classList.remove('recording');
        btn.title = 'Start Recording';
        // Change back to circle (record icon)
        btn.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24">
            <circle cx="12" cy="12" r="8" fill="#FF3B30"/>
        </svg>`;
    }
}

function updatePlaybackButton() {
    const btn = document.getElementById('btn-playback');
    if (!btn) return;
    
    // Always show play icon and always open MIDI file when clicked
    btn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>`;
    btn.title = 'Open MIDI File';
    btn.disabled = false;
    btn.style.opacity = '1';
    btn.onclick = () => openMIDIFile();
}

// =============================================================================
// GETTERS
// =============================================================================

export function getIsRecording() {
    return isRecording;
}

export function getIsPlaying() {
    return isPlaying;
}

export function getRecordedEvents() {
    return recordedEvents;
}

export function getRecordingCount() {
    return recordedEvents.length;
}

// =============================================================================
// LOCALSTORAGE PERSISTENCE
// =============================================================================

export function saveRecording() {
    if (recordedEvents.length === 0) return;
    
    const recording = {
        version: 1,
        timestamp: Date.now(),
        duration: (recordedEvents[recordedEvents.length - 1]?.timestamp || 0) / 1000,
        eventCount: recordedEvents.length,
        events: recordedEvents
    };
    
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(recording));
        console.log(`[Recorder] Saved ${recording.eventCount} events (${recording.duration.toFixed(1)}s)`);
        return true;
    } catch (e) {
        if (e.name === 'QuotaExceededError') {
            console.error('[Recorder] Storage quota exceeded');
            if (window.showToast) {
                window.showToast('⚠️ Recording too large to save');
            }
        } else {
            console.error('[Recorder] Failed to save recording:', e);
        }
        return false;
    }
}

export function loadRecording(silent = false) {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) {
            console.log('[Recorder] No saved recording found');
            return false;
        }
        
        const recording = JSON.parse(data);
        
        // Version check
        if (recording.version !== 1) {
            console.warn('[Recorder] Incompatible recording version');
            return false;
        }
        
        // Validate events array
        if (!Array.isArray(recording.events)) {
            throw new Error('Invalid recording format');
        }
        
        recordedEvents = recording.events;
        
        console.log(`[Recorder] Loaded ${recordedEvents.length} events (${recording.duration.toFixed(1)}s)`);
        
        // Update UI
        updatePlaybackButton();
        
        // Only show toast if not silent (e.g., not on startup)
        if (!silent && window.showToast) {
            window.showToast(`📼 Recording loaded (${recording.duration.toFixed(1)}s)`);
        }
        
        return true;
    } catch (e) {
        console.error('[Recorder] Failed to load recording:', e);
        // Clear corrupted data
        try {
            localStorage.removeItem(STORAGE_KEY);
            console.log('[Recorder] Cleared corrupted recording data');
        } catch (clearError) {
            console.error('[Recorder] Failed to clear corrupted data:', clearError);
        }
        return false;
    }
}

export function getRecordingSize() {
    if (recordedEvents.length === 0) return 0;
    
    const recording = {
        version: 1,
        timestamp: Date.now(),
        duration: (recordedEvents[recordedEvents.length - 1]?.timestamp || 0) / 1000,
        eventCount: recordedEvents.length,
        events: recordedEvents
    };
    
    const json = JSON.stringify(recording);
    // Size in KB
    return (new Blob([json]).size / 1024).toFixed(2);
}

// =============================================================================
// MIDI FILE EXPORT/IMPORT
// =============================================================================

function encodeVLQ(value) {
    if (value === 0) return [0];
    const bytes = [];
    let v = value;
    while (v > 0) {
        bytes.unshift(v & 0x7F);
        v >>= 7;
    }
    for (let i = 0; i < bytes.length - 1; i++) {
        bytes[i] |= 0x80;
    }
    return bytes;
}

function buildMIDIFile(events) {
    const ticksPerBeat = 480;
    const tempo = 120; // BPM
    const msPerTick = (60000 / tempo) / ticksPerBeat;
    
    // Convert timestamps to ticks and sort
    const midiEvents = events.map(e => ({
        ...e,
        ticks: Math.round(e.timestamp / msPerTick)
    })).sort((a, b) => a.ticks - b.ticks);
    
    // Build track data
    const trackData = [];
    let lastTick = 0;
    
    // Add tempo meta event (120 BPM = 500000 microseconds per beat)
    trackData.push(0x00, 0xFF, 0x51, 0x03, 0x07, 0xA1, 0x20);
    
    midiEvents.forEach(event => {
        const deltaTicks = Math.max(0, event.ticks - lastTick);
        lastTick = event.ticks;
        
        trackData.push(...encodeVLQ(deltaTicks));
        
        if (event.type === 'noteOn') {
            trackData.push(0x90 | (event.channel || 0));
            trackData.push(event.midi);
            trackData.push(event.velocity || 100);
        } else if (event.type === 'noteOff') {
            trackData.push(0x80 | (event.channel || 0));
            trackData.push(event.midi);
            trackData.push(0);
        }
    });
    
    // End of track
    trackData.push(0x00, 0xFF, 0x2F, 0x00);
    
    // Header chunk
    const header = [
        0x4D, 0x54, 0x68, 0x64,  // "MThd"
        0x00, 0x00, 0x00, 0x06,  // Length = 6
        0x00, 0x00,              // Format 0
        0x00, 0x01,              // 1 track
        (ticksPerBeat >> 8) & 0xFF, ticksPerBeat & 0xFF
    ];
    
    // Track chunk
    const trackHeader = [
        0x4D, 0x54, 0x72, 0x6B,  // "MTrk"
        (trackData.length >> 24) & 0xFF,
        (trackData.length >> 16) & 0xFF,
        (trackData.length >> 8) & 0xFF,
        trackData.length & 0xFF
    ];
    
    return new Uint8Array([...header, ...trackHeader, ...trackData]);
}

// Storage key for MIDI files in localStorage
const MIDI_STORAGE_KEY = 'janko_midi_files';

// Get all saved MIDI files from localStorage
function getSavedMIDIFiles() {
    try {
        const data = localStorage.getItem(MIDI_STORAGE_KEY);
        return data ? JSON.parse(data) : [];
    } catch (e) {
        console.error('[Recorder] Failed to get saved files:', e);
        return [];
    }
}

// Save MIDI file to localStorage
function saveMIDIToStorage(filename, base64Data) {
    try {
        const files = getSavedMIDIFiles();
        
        // Add new file (or update if exists)
        const existingIndex = files.findIndex(f => f.name === filename);
        const fileEntry = {
            name: filename,
            data: base64Data,
            timestamp: Date.now()
        };
        
        if (existingIndex >= 0) {
            files[existingIndex] = fileEntry;
        } else {
            files.unshift(fileEntry); // Add to beginning (newest first)
        }
        
        // Keep only last 20 files to avoid storage limits
        const trimmedFiles = files.slice(0, 20);
        
        localStorage.setItem(MIDI_STORAGE_KEY, JSON.stringify(trimmedFiles));
        console.log(`[Recorder] Saved ${filename} to localStorage (${trimmedFiles.length} files total)`);
        return true;
    } catch (e) {
        console.error('[Recorder] Failed to save to localStorage:', e);
        return false;
    }
}

// Load MIDI file from localStorage
function loadMIDIFromStorage(filename) {
    try {
        const files = getSavedMIDIFiles();
        const file = files.find(f => f.name === filename);
        if (file) {
            // Convert base64 to Uint8Array
            const binaryString = atob(file.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
        }
        return null;
    } catch (e) {
        console.error('[Recorder] Failed to load from localStorage:', e);
        return null;
    }
}

async function exportMIDINative(filename, midiData) {
    try {
        const { Filesystem } = window.Capacitor.Plugins;
        
        // Convert Uint8Array to base64
        const base64 = btoa(String.fromCharCode(...midiData));
        
        const folderPath = 'Janko Piano';
        const filePath = `${folderPath}/${filename}`;
        
        console.log(`[Recorder] Saving to Documents/${filePath}`);
        
        // Create directory if it doesn't exist
        try {
            await Filesystem.mkdir({
                path: folderPath,
                directory: 'DOCUMENTS',
                recursive: true
            });
            console.log(`[Recorder] Directory ensured: Documents/${folderPath}`);
        } catch (mkdirError) {
            console.log(`[Recorder] Directory note: ${mkdirError.message}`);
        }
        
        // Write file to Documents/Janko Piano/ (no encoding = binary/base64)
        await Filesystem.writeFile({
            path: filePath,
            data: base64,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] File saved successfully: Documents/${filePath}`);
        
        // Get file URI for verification
        const fileUri = await Filesystem.getUri({
            path: filePath,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] File URI: ${fileUri.uri}`);
        
        if (window.showToast) {
            window.showToast(`✅ Saved to:\nDocuments/Janko Piano/\n${filename}`);
        }
        
        return true;
    } catch (error) {
        console.error('[Recorder] Save failed:', error);
        console.error('[Recorder] Error details:', error.message);
        if (window.showToast) {
            window.showToast(`⚠️ Save failed:\n${error.message}`);
        }
        return false;
    }
}

function exportMIDIWeb(filename, midiData) {
    // Convert to base64 and save to localStorage
    const base64 = btoa(String.fromCharCode(...midiData));
    saveMIDIToStorage(filename, base64);
    
    // Web download using blob
    const blob = new Blob([midiData], { type: 'audio/midi' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log(`[Recorder] Web export successful: ${filename}`);
    
    // Show success toast
    if (window.showToast) {
        window.showToast(`✅ Saved: ${filename}`);
    }
}

export async function exportMIDI(filename = 'recording.mid') {
    if (recordedEvents.length === 0) {
        if (window.showToast) {
            window.showToast('⚠️ No recording to export');
        }
        return;
    }
    
    const midiData = buildMIDIFile(recordedEvents);
    
    // Check if running in Capacitor native app
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    
    if (isNative) {
        // Use native file system + share
        await exportMIDINative(filename, midiData);
    } else {
        // Use web download
        exportMIDIWeb(filename, midiData);
    }
}

export function importMIDI(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                // Stop any previous playback
                if (isPlaying) {
                    stopPlayback();
                }
                
                const data = new Uint8Array(e.target.result);
                const events = parseMIDIFile(data);
                
                recordedEvents = events;
                
                // Clear saved recording from localStorage to free memory
                try {
                    localStorage.removeItem(STORAGE_KEY);
                    console.log('[Recorder] Cleared saved recording from localStorage');
                } catch (clearErr) {
                    console.warn('[Recorder] Failed to clear localStorage:', clearErr);
                }
                
                updatePlaybackButton();
                updateRecordButton();
                
                if (window.showToast) {
                    const duration = (events[events.length - 1]?.timestamp || 0) / 1000;
                    window.showToast(`📂 Loaded ${file.name} (${duration.toFixed(1)}s)`);
                }
                
                console.log(`[Recorder] Imported ${events.length} events from ${file.name}`);
                resolve(events);
            } catch (err) {
                console.error('[Recorder] Import failed:', err);
                if (window.showToast) {
                    window.showToast('⚠️ Invalid MIDI file');
                }
                reject(err);
            }
        };
        
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });
}

function parseMIDIFile(data) {
    let pos = 0;
    
    // Validate header
    const headerStr = String.fromCharCode(data[0], data[1], data[2], data[3]);
    if (headerStr !== 'MThd') {
        throw new Error('Invalid MIDI header');
    }
    
    pos = 8;
    const format = (data[pos] << 8) | data[pos + 1];
    const numTracks = (data[pos + 2] << 8) | data[pos + 3];
    const division = (data[pos + 4] << 8) | data[pos + 5];
    pos += 6;
    
    const ticksPerBeat = division;
    let tempo = 500000; // Default: 120 BPM
    const events = [];
    
    // Parse tracks
    for (let t = 0; t < numTracks; t++) {
        const trackStr = String.fromCharCode(data[pos], data[pos+1], data[pos+2], data[pos+3]);
        if (trackStr !== 'MTrk') {
            throw new Error('Invalid track header');
        }
        
        const trackLength = (data[pos + 4] << 24) | (data[pos + 5] << 16) | 
                           (data[pos + 6] << 8) | data[pos + 7];
        pos += 8;
        
        const trackEnd = pos + trackLength;
        let currentTick = 0;
        let runningStatus = 0;
        
        while (pos < trackEnd) {
            // Read delta time (VLQ)
            let deltaTime = 0;
            let byte;
            do {
                byte = data[pos++];
                deltaTime = (deltaTime << 7) | (byte & 0x7F);
            } while (byte & 0x80);
            
            currentTick += deltaTime;
            const msPerTick = tempo / ticksPerBeat / 1000;
            const timestamp = Math.round(currentTick * msPerTick);
            
            // Read event
            let status = data[pos];
            if (status < 0x80) {
                status = runningStatus;
            } else {
                pos++;
                runningStatus = status;
            }
            
            const command = status & 0xF0;
            const channel = status & 0x0F;
            
            if (command === 0x90) { // Note On
                const note = data[pos++];
                const velocity = data[pos++];
                
                if (velocity > 0) {
                    events.push({ type: 'noteOn', timestamp, midi: note, velocity, channel });
                } else {
                    events.push({ type: 'noteOff', timestamp, midi: note, velocity: 0, channel });
                }
            } else if (command === 0x80) { // Note Off
                const note = data[pos++];
                pos++; // velocity
                events.push({ type: 'noteOff', timestamp, midi: note, velocity: 0, channel });
            } else if (command === 0xC0) { // Program Change
                const program = data[pos++];
                events.push({ type: 'programChange', timestamp, channel, program });
                console.log(`[MIDI Parse] Program Change: Channel ${channel} -> Program ${program}`);
            } else if (status === 0xFF) { // Meta event
                const metaType = data[pos++];
                let metaLength = 0;
                let b;
                do {
                    b = data[pos++];
                    metaLength = (metaLength << 7) | (b & 0x7F);
                } while (b & 0x80);
                
                if (metaType === 0x51 && metaLength === 3) { // Tempo
                    tempo = (data[pos] << 16) | (data[pos + 1] << 8) | data[pos + 2];
                }
                pos += metaLength;
                
                if (metaType === 0x2F) break; // End of track
            } else if (command === 0xD0) { // Channel Pressure (1 data byte)
                pos++; // 1 data byte
            } else if (command >= 0x80) {
                pos += 2; // 2 data bytes (other commands)
            }
        }
        pos = trackEnd;
    }
    
    return events.sort((a, b) => a.timestamp - b.timestamp);
}

// Show custom file picker from Documents/Janko Piano/
async function showMIDIFileList() {
    console.log('[Recorder] showMIDIFileList called');
    
    try {
        const { Filesystem } = window.Capacitor.Plugins;
        const folderPath = 'Janko Piano';
        
        console.log(`[Recorder] Reading from Documents/${folderPath}`);
        
        // Ensure directory exists
        try {
            await Filesystem.mkdir({
                path: folderPath,
                directory: 'DOCUMENTS',
                recursive: true
            });
        } catch (mkdirError) {
            console.log(`[Recorder] Directory check: ${mkdirError.message}`);
        }
        
        // Read directory
        const result = await Filesystem.readdir({
            path: folderPath,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] Found ${result.files.length} files`);
        
        // Filter MIDI files
        const midiFiles = result.files
            .filter(file => {
                const name = file.name || file;
                return name.endsWith('.mid') || name.endsWith('.midi');
            })
            .map(file => {
                const name = file.name || file;
                // Extract timestamp from filename if present
                const match = name.match(/janko_(\d{8}T\d{6})/);
                let timestamp = Date.now();
                if (match) {
                    const ts = match[1];
                    const year = ts.substr(0, 4);
                    const month = ts.substr(4, 2);
                    const day = ts.substr(6, 2);
                    const hour = ts.substr(9, 2);
                    const minute = ts.substr(11, 2);
                    const second = ts.substr(13, 2);
                    timestamp = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`).getTime();
                }
                return { name, timestamp };
            })
            .sort((a, b) => b.timestamp - a.timestamp); // Newest first
        
        console.log(`[Recorder] Found ${midiFiles.length} MIDI files`);
        
        if (midiFiles.length === 0) {
            if (window.showToast) {
                window.showToast('📂 No recordings yet\nRecord something first!');
            }
            return;
        }
        
        // Show custom file picker modal
        if (window.showMIDIFilePicker) {
            console.log('[Recorder] Showing file picker with', midiFiles.length, 'files');
            window.showMIDIFilePicker(midiFiles, async (selectedFilename) => {
                console.log('[Recorder] File selected:', selectedFilename);
                await loadMIDIFromDocuments(selectedFilename);
            });
        } else {
            console.error('[Recorder] showMIDIFilePicker not found!');
            await loadMIDIFromDocuments(midiFiles[0].name);
        }
        
    } catch (error) {
        console.error('[Recorder] Failed to list files:', error);
        if (window.showToast) {
            window.showToast('📂 No recordings yet\nRecord something first!');
        }
    }
}

// Load MIDI file from Documents/Janko Piano/ and start playback
async function loadMIDIFromDocuments(filename) {
    console.log(`[Recorder] Loading ${filename} from Documents/Janko Piano/`);
    
    try {
        // Stop any previous playback
        if (isPlaying) {
            stopPlayback();
        }
        
        const { Filesystem } = window.Capacitor.Plugins;
        const folderPath = 'Janko Piano';
        const filePath = `${folderPath}/${filename}`;
        
        // Read file (no encoding = returns base64)
        const fileData = await Filesystem.readFile({
            path: filePath,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] File read, size: ${fileData.data.length} bytes`);
        
        // Convert base64 to Uint8Array
        const binaryString = atob(fileData.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Parse MIDI file
        const events = parseMIDIFile(bytes);
        recordedEvents = events;
        
        // Clear saved recording from localStorage to free memory
        try {
            localStorage.removeItem(STORAGE_KEY);
            console.log('[Recorder] Cleared saved recording from localStorage');
        } catch (e) {
            console.warn('[Recorder] Failed to clear localStorage:', e);
        }
        
        updatePlaybackButton();
        updateRecordButton();
        
        const duration = (events[events.length - 1]?.timestamp || 0) / 1000;
        console.log(`[Recorder] Loaded ${events.length} events, duration: ${duration.toFixed(1)}s`);
        
        if (window.showToast) {
            window.showToast(`📂 Loaded: ${filename}\n⏱️ ${duration.toFixed(1)}s`);
        }
        
        // Close settings panel to see key playing
        if (window.closeSettings) {
            window.closeSettings();
        }
        if (window.closeQuickSettings) {
            window.closeQuickSettings();
        }
        
        // Auto-start playback
        startPlayback();
        
        return true;
    } catch (error) {
        console.error('[Recorder] Failed to load file:', error);
        if (window.showToast) {
            window.showToast(`⚠️ Failed to load:\n${error.message}`);
        }
        return false;
    }
}

function openMIDIFileWeb() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.mid,.midi';
    input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
            importMIDI(file).then(() => {
                // Close settings panel to see key playing
                if (window.closeSettings) {
                    window.closeSettings();
                }
                if (window.closeQuickSettings) {
                    window.closeQuickSettings();
                }
                // Auto-start playback after import
                startPlayback();
            }).catch(err => {
                console.error('Import failed:', err);
            });
        }
    };
    input.click();
}

export function openMIDIFile() {
    console.log('[Recorder] openMIDIFile called');
    
    // Check if running in Capacitor native app
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    
    if (isNative) {
        // Native: Use custom file picker from Documents/Janko Piano/
        showMIDIFileList();
    } else {
        // Web: Use native browser file picker
        openMIDIFileWeb();
    }
}

export function openNativeFileBrowser() {
    // Force open native file browser
    openMIDIFileWeb();
}

// Delete MIDI file from Documents/Janko Piano/
async function deleteMIDIFile(filename) {
    console.log(`[Recorder] Deleting ${filename}`);
    
    try {
        const { Filesystem } = window.Capacitor.Plugins;
        const folderPath = 'Janko Piano';
        const filePath = `${folderPath}/${filename}`;
        
        // Delete file
        await Filesystem.deleteFile({
            path: filePath,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] File deleted: ${filename}`);
        
        if (window.showToast) {
            window.showToast(`🗑️ Deleted: ${filename}`);
        }
        
        // Refresh the file list
        setTimeout(() => showMIDIFileList(), 500);
        
        return true;
    } catch (error) {
        console.error('[Recorder] Failed to delete file:', error);
        if (window.showToast) {
            window.showToast(`⚠️ Delete failed:\n${error.message}`);
        }
        return false;
    }
}

// Share MIDI file from Documents/Janko Piano/
async function shareMIDIFile(filename) {
    console.log(`[Recorder] Sharing ${filename}`);
    
    try {
        const { Filesystem, Share } = window.Capacitor.Plugins;
        const folderPath = 'Janko Piano';
        const filePath = `${folderPath}/${filename}`;
        
        // Get file URI
        const fileUri = await Filesystem.getUri({
            path: filePath,
            directory: 'DOCUMENTS'
        });
        
        console.log(`[Recorder] File URI: ${fileUri.uri}`);
        
        // Open share sheet
        await Share.share({
            title: 'Share MIDI Recording',
            text: `Janko Piano recording: ${filename}`,
            url: fileUri.uri,
            dialogTitle: 'Share MIDI File'
        });
        
        console.log(`[Recorder] Share sheet opened for: ${filename}`);
        return true;
    } catch (error) {
        console.error('[Recorder] Failed to share file:', error);
        if (window.showToast) {
            window.showToast(`⚠️ Share failed:\n${error.message}`);
        }
        return false;
    }
}

// Expose functions to window
window.deleteMIDIFile = deleteMIDIFile;
window.shareMIDIFile = shareMIDIFile;
